import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FlightsPage() {
  const [searchParams] = useSearchParams();
  const [flightType, setFlightType] = useState('roundtrip');
  const [from, setFrom] = useState(searchParams.get('origin') || '');
  const [to, setTo] = useState(searchParams.get('destination') || '');
  const [departDate, setDepartDate] = useState(searchParams.get('date') || '');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState({ adults: parseInt(searchParams.get('passengers') || '1'), children: 0, infants: 0 });
  const [showPassengers, setShowPassengers] = useState(false);
  const [cabinClass, setCabinClass] = useState('economy');
  
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');

  // Automatically search if params are provided
  useEffect(() => {
    if (searchParams.get('origin') && searchParams.get('destination') && searchParams.get('date')) {
      handleSearch();
    }
  }, []); // Run once on mount

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!from || !to || !departDate) return;

    setLoading(true);
    setError('');
    setHasSearched(true);
    setSearchResults([]);

    try {
      const payload: any = {
        origin: from,
        destination: to,
        date: departDate,
        passengers: passengers.adults
      };
      if (flightType === 'roundtrip' && returnDate) {
        payload.return_date = returnDate;
      }
      const response = await axios.post('/api/travel/search-flights', payload);
      // The backend returns { results: [...] }
      if (response.data && response.data.results) {
        setSearchResults(response.data.results);
      } else {
        setSearchResults([]);
      }
    } catch (err: any) {
      console.error(err);
      setError('Could not find flights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <Header />
      
      {/* Premium Hero Section */}
      <section 
        className="relative pt-32 pb-24 bg-navy-900 bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(10, 25, 47, 0.9), rgba(10, 25, 47, 0.6)), url('https://readdy.ai/api/search-image?query=luxury%20airplane%20wing%20above%20clouds%20sunset&width=1920&height=600&seq=flights-premium&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 bg-navy-900/40"></div>
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 animate-fade-in-up">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg tracking-tight">
            Find Your Next <span className="text-logo-gold-400">Flight</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 font-light drop-shadow-md">
            Premium travel experiences start with the perfect booking.
          </p>
        </div>
      </section>

      {/* Glassmorphism Search Section */}
      <section className="relative z-20 -mt-16 mb-16 mx-4">
        <div className="bg-white/80 backdrop-blur-xl border border-white/40 rounded-3xl shadow-2xl p-6 md:p-8 max-w-6xl mx-auto">
          <form onSubmit={handleSearch} className="space-y-6">
            <div className="flex space-x-6 text-sm font-semibold text-navy-800">
              <label className="flex items-center cursor-pointer hover:text-logo-gold-600 transition-colors">
                <input type="radio" name="flightType" value="roundtrip" checked={flightType === 'roundtrip'} onChange={(e) => setFlightType(e.target.value)} className="mr-2 accent-logo-gold-500" />
                Round-trip
              </label>
              <label className="flex items-center cursor-pointer hover:text-logo-gold-600 transition-colors">
                <input type="radio" name="flightType" value="oneway" checked={flightType === 'oneway'} onChange={(e) => setFlightType(e.target.value)} className="mr-2 accent-logo-gold-500" />
                One-way
              </label>
            </div>
            
            <div className={`grid grid-cols-1 gap-6 ${flightType === 'roundtrip' ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
              <div className="relative">
                <i className="ri-takeoff-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <input placeholder="Origin (e.g. LON)" value={from} onChange={(e) => setFrom(e.target.value.toUpperCase())} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
              </div>
              <div className="relative">
                <i className="ri-flight-land-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <input placeholder="Destination (e.g. BCN)" value={to} onChange={(e) => setTo(e.target.value.toUpperCase())} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
              </div>
              <div className="relative">
                <i className="ri-calendar-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <input type="date" placeholder="Departure" value={departDate} onChange={(e) => setDepartDate(e.target.value)} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
              </div>
              {flightType === 'roundtrip' && (
                <div className="relative">
                  <i className="ri-calendar-check-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                  <input type="date" placeholder="Return" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className="w-full bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all" required />
                </div>
              )}
              <div className="relative">
                <i className="ri-user-line absolute left-4 top-1/2 transform -translate-y-1/2 text-logo-gold-500 text-xl"></i>
                <button type="button" onClick={() => setShowPassengers(!showPassengers)} className="w-full text-left bg-white text-navy-900 pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm transition-all">
                  {passengers.adults} Adult{passengers.adults > 1 ? 's' : ''}
                </button>
                {/* Passengers Popover */}
                {showPassengers && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 z-50 w-64 animate-fade-in">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-semibold text-navy-900">Adults</span>
                      <div className="flex items-center space-x-3">
                        <button type="button" onClick={() => setPassengers(p => ({ ...p, adults: Math.max(1, p.adults - 1) }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">-</button>
                        <span>{passengers.adults}</span>
                        <button type="button" onClick={() => setPassengers(p => ({ ...p, adults: p.adults + 1 }))} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-navy-900 font-bold">+</button>
                      </div>
                    </div>
                    <button type="button" onClick={() => setShowPassengers(false)} className="w-full bg-navy-900 text-white py-2 rounded-lg hover:bg-navy-800 transition-colors">Done</button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end">
              <button type="submit" disabled={loading} className="bg-gradient-to-r from-logo-gold-500 to-logo-gold-600 hover:from-logo-gold-400 hover:to-logo-gold-500 text-navy-900 font-bold text-lg px-12 py-4 rounded-xl shadow-lg transition-transform transform hover:scale-105 disabled:opacity-70 disabled:hover:scale-100 flex items-center">
                {loading ? <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i> : <i className="ri-search-line mr-2"></i>}
                {loading ? 'Searching...' : 'Find Flights'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Search Results */}
      <section className="flex-grow max-w-6xl mx-auto w-full px-4 mb-20">
        {hasSearched && !loading && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-navy-900 mb-6">
              {searchResults.length > 0 ? `Found ${searchResults.length} Flights` : 'No Flights Found'}
            </h2>
            
            {error && (
               <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 mb-6">{error}</div>
            )}

            <div className="space-y-6">
              {searchResults.map((flight, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow p-6 flex flex-col md:flex-row items-center justify-between border border-gray-100 group animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  
                  {/* Airline Info */}
                  <div className="flex items-center w-full md:w-1/4 mb-4 md:mb-0">
                    <img src={flight.logo || 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/Emirates_logo.svg/200px-Emirates_logo.svg.png'} alt="Airline Logo" className="h-12 w-12 object-contain mr-4 rounded-lg bg-gray-50 p-1" />
                    <div>
                      <h3 className="font-bold text-navy-900 text-lg">{flight.airline || 'LetSFG Provider'}</h3>
                      <p className="text-sm text-gray-500">{flight.flightNumber || 'Direct'}</p>
                    </div>
                  </div>

                  {/* Flight Times */}
                  <div className="flex items-center justify-between w-full md:w-2/4 px-4 md:px-8 mb-6 md:mb-0">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-navy-900">{flight.departureTime || '10:00'}</p>
                      <p className="text-gray-500 font-medium">{from}</p>
                    </div>
                    
                    <div className="flex-grow mx-6 relative flex flex-col items-center">
                      <p className="text-xs text-gray-400 font-semibold mb-1">{flight.duration || '2h 00m'}</p>
                      <div className="w-full h-px bg-gray-300 relative">
                        <i className="ri-plane-fill absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-logo-gold-500 text-xl group-hover:text-logo-gold-600 transition-colors"></i>
                      </div>
                      <p className="text-xs text-gray-400 font-semibold mt-1">{flight.stops === 0 ? 'Direct' : '1 Stop'}</p>
                    </div>

                    <div className="text-center">
                      <p className="text-2xl font-bold text-navy-900">{flight.arrivalTime || '12:00'}</p>
                      <p className="text-gray-500 font-medium">{to}</p>
                    </div>
                  </div>

                  {/* Price and Book */}
                  <div className="flex flex-col items-end w-full md:w-1/4 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6">
                    <p className="text-sm text-gray-500 mb-1">Total Price</p>
                    <p className="text-3xl font-extrabold text-navy-900 mb-3 text-right">
                      ₦{(flight.retailPrice || flight.price || 0).toLocaleString()}
                    </p>
                    <button className="w-full bg-navy-900 hover:bg-navy-800 text-white font-bold py-3 rounded-xl transition-colors shadow-md">
                      Book Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
